import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import { isPathExemptFromAccessCheck, resolveNavKeyForLocation } from '../config/navigationCatalog';
import { expandPermissionKeys, isNavKeyGranted } from '../utils/accessControl';

interface AccessControlContextValue {
  permissions: Set<string>;
  isLoading: boolean;
  isReady: boolean;
  canAccess: (key: string) => boolean;
  canAccessPath: (pathname: string, search?: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const AccessControlContext = createContext<AccessControlContextValue | undefined>(undefined);

export const AccessControlProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, token, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated || !token || authLoading) {
      setPermissions(new Set());
      setIsReady(true);
      return;
    }

    if (user?.context === 'backoffice') {
      setPermissions(new Set(['*']));
      setIsReady(true);
      return;
    }

    setIsLoading(true);
    setIsReady(false);
    try {
      const res = await apiService.getMyPermissions();
      const list = res.permissions ?? [];
      setPermissions(expandPermissionKeys(Array.isArray(list) ? list : []));
    } catch {
      setPermissions(new Set());
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }, [isAuthenticated, token, authLoading, user?.context]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!isAuthenticated || !user) {
      setPermissions(new Set());
      setIsReady(true);
      return;
    }
    void refreshPermissions();
  }, [authLoading, isAuthenticated, user?.id, user?.context, refreshPermissions]);

  const canAccess = useCallback(
    (key: string) => {
      if (!isReady || authLoading) {
        return false;
      }
      if (user?.context === 'backoffice') {
        return true;
      }
      if (user?.dealershipRole === 'owner') {
        return true;
      }
      return isNavKeyGranted(key, permissions);
    },
    [permissions, user?.context, user?.dealershipRole, isReady, authLoading],
  );

  const canAccessPath = useCallback(
    (pathname: string, search = '') => {
      if (!isReady || authLoading) {
        return false;
      }
      if (isPathExemptFromAccessCheck(pathname)) {
        return true;
      }
      const navKey = resolveNavKeyForLocation(pathname, search);
      if (!navKey) {
        return false;
      }
      return canAccess(navKey);
    },
    [canAccess, isReady, authLoading],
  );

  const value = useMemo(
    () => ({
      permissions,
      isLoading: isLoading || authLoading,
      isReady,
      canAccess,
      canAccessPath,
      refreshPermissions,
    }),
    [permissions, isLoading, authLoading, isReady, canAccess, canAccessPath, refreshPermissions],
  );

  return (
    <AccessControlContext.Provider value={value}>{children}</AccessControlContext.Provider>
  );
};

export const useAccessControl = (): AccessControlContextValue => {
  const ctx = useContext(AccessControlContext);
  if (!ctx) {
    throw new Error('useAccessControl must be used within AccessControlProvider');
  }
  return ctx;
};
